import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AccountCertificationQuery,
  AccountCertificationsQueryBuilder,
  ListAccountCertificationsResponse,
  QueryAccountCertificationsResponse,
  listAccountCertifications as universalListAccountCertifications,
  queryAccountCertifications as universalQueryAccountCertifications,
  typedQueryAccountCertifications as universalTypedQueryAccountCertifications,
} from './academy-certifications-v1-account-certification-account-certifications.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/account-certifications' };

export function queryAccountCertifications(
  httpClient: HttpClient
): QueryAccountCertificationsSignature {
  return () =>
    universalQueryAccountCertifications(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryAccountCertificationsSignature {
  /**
   * Retrieves a list of up to 1,000 account certifications, given the provided paging, filtering, and sorting.
   *
   * Query Account Certifications returns the full account certification records, including the underlying `userCertificationIds`, for the caller's own account only. To retrieve any account's certifications as a public, visitor-safe projection, call [List Account Certifications](https://dev.wix.com/docs/api-reference/account-level/studio-workspace/certifications/account-certification-v1/list-account-certifications) instead.
   *
   * Query Account Certifications runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order
   * - `cursorPaging.limit` is `50`
   *
   * You can filter and sort by `id`, `certificationType`, and `createdDate`.
   *
   * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   */
  (): AccountCertificationsQueryBuilder;
}

export function typedQueryAccountCertifications(
  httpClient: HttpClient
): TypedQueryAccountCertificationsSignature {
  return (query: AccountCertificationQuery) =>
    universalTypedQueryAccountCertifications(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryAccountCertificationsSignature {
  /** */
  (query: AccountCertificationQuery): Promise<
    NonNullablePaths<
      QueryAccountCertificationsResponse,
      | `accountCertifications`
      | `accountCertifications.${number}.certificationType`,
      4
    >
  >;
}

export function listAccountCertifications(
  httpClient: HttpClient
): ListAccountCertificationsSignature {
  return (accountId: string) =>
    universalListAccountCertifications(
      accountId,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAccountCertificationsSignature {
  /**
   * Retrieves an account's certifications for public display, given the account ID.
   *
   * This is a public, visitor-safe projection that returns only each certification's type and
   * creation date. To retrieve the full account certification records, including the underlying
   * `userCertificationIds`, call [Query Account Certifications](https://dev.wix.com/docs/api-reference/account-level/studio-workspace/certifications/account-certification-v1/query-account-certifications) instead.
   * @param - ID of the account to get certifications for.
   */
  (accountId: string): Promise<
    NonNullablePaths<
      ListAccountCertificationsResponse,
      `certifications` | `certifications.${number}.certificationType`,
      4
    >
  >;
}

export {
  AccountCertification,
  AccountCertificationQuerySpec,
  AccountCertificationsQueryBuilder,
  AccountCertificationsQueryResult,
  AccountInfo,
  ActionEvent,
  Certification,
  CertificationType,
  CertifyAccountRequest,
  CertifyAccountResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DecertifyAccountRequest,
  DecertifyAccountResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListAccountCertificationsRequest,
  ListAccountCertificationsResponse,
  MessageEnvelope,
  QueryAccountCertificationsRequest,
  QueryAccountCertificationsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  TeamMemberRemovedEvent,
  WebhookIdentityType,
  utils,
} from './academy-certifications-v1-account-certification-account-certifications.universal.js';
